/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme, type ThemePreference } from '@vaas/react-ui-kit';

import i18n from '@/i18n/i18n';
import { useUserId } from '@/api/hooks';
import { getUserSettings } from '@/api/settings';

/**
 * Narrows whatever the settings API returns to a preference the theme provider accepts.
 *
 * `system` is passed through rather than resolved to light or dark. The provider keeps following
 * the OS from then on, so a user who chose "system" and then changes their OS to dark sees the app
 * follow - which is what they asked for, and what resolving the value once at load could not do.
 */
function toThemePreference(value: string | null | undefined): ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'light';
}

/**
 * Applies saved user settings from the backend. The settings query is the theme source of truth;
 * language is applied once per account so header switches are not clobbered.
 */
export function useSyncUserSettings(): void {
  const gmin = useUserId();
  const { setTheme } = useTheme();
  const appliedLangFor = useRef<string | null>(null);
  const { data } = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled: Boolean(gmin),
    retry: false,
  });

  useEffect(() => {
    if (!data) return;

    // Theme: the settings cache is authoritative, so resolve and apply it on every change.
    setTheme(toThemePreference(data.themeSetting));

    // Language: apply once per account so a mid-session switch isn't clobbered by a refetch.
    if (appliedLangFor.current !== gmin) {
      appliedLangFor.current = gmin;
      const code = data.languageDisplay?.languageCode;
      if (code && i18n.language !== code) {
        void i18n.changeLanguage(code);
      }
    }
  }, [data, gmin, setTheme]);
}
